import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Button,
  TextField,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useCollection } from '../hooks/useFirestore';
import { query as fbQuery, orderBy as fbOrderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, doc, serverTimestamp, deleteField } from 'firebase/firestore';
import { DateTimePicker } from '@mui/x-date-pickers';
import BlockEditor, { ContentBlock } from '../components/blocks/BlockEditor';
import MediaLibraryDialog from '../components/blocks/MediaLibraryDialog';
import { renderBlocks } from '../components/blocks/renderBlocks';

interface ImportantNotice {
  id: string;
  title?: string;
  content?: string;
  blocks?: ContentBlock[];
  // legacy
  target?: 'school' | 'student' | 'both' | 'lp';
  // new
  targets?: string[];
  publishedAt: Date;
  expiresAt?: Date;
  createdAt: Date;
}

const TARGET_PRESETS: { value: string; label: string }[] = [
  { value: 'school', label: '教室' },
  { value: 'student', label: '生徒' },
  { value: 'lp', label: 'LP' },
];

function uniq(arr: string[]): string[] {
  return Array.from(new Set(arr.filter(Boolean)));
}

function resolveTargetsFromDoc(notice: any): string[] {
  const v = notice?.targets;
  if (Array.isArray(v)) return uniq(v.map(String));
  const legacy = String(notice?.target || '');
  if (!legacy) return ['school', 'student'];
  if (legacy === 'both') return ['school', 'student'];
  return [legacy];
}

function computeLegacyTargetFromTargets(targets: string[]): 'school' | 'student' | 'both' | 'lp' | undefined {
  const t = uniq(targets.map(String));
  if (t.length === 1) {
    const only = t[0];
    if (only === 'school' || only === 'student' || only === 'lp') return only;
    return undefined;
  }
  if (t.length === 2 && t.includes('school') && t.includes('student')) return 'both';
  return undefined;
}

export default function ImportantNoticeEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { data: notices } = useCollection<ImportantNotice>(
    'importantNotices',
    (q) => fbQuery(q, fbOrderBy('publishedAt', 'desc'))
  );

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    blocks: [] as ContentBlock[],
    targets: ['school', 'student'] as string[],
    publishedAt: new Date(),
    expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) as Date | null, // 30日後
  });

  const [mediaOpen, setMediaOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // 編集時は既存データを読み込み
  useEffect(() => {
    if (isEdit && notices) {
      const notice = notices.find(n => n.id === id);
      if (notice) {
        setFormData({
          title: (notice as any).title || '',
          content: notice.content || '',
          blocks: (notice as any).blocks || [],
          targets: resolveTargetsFromDoc(notice),
          publishedAt: ((): Date => {
            const v: any = (notice as any).publishedAt;
            try { return typeof v?.toDate === 'function' ? v.toDate() : new Date(v); } catch { return new Date(); }
          })(),
          expiresAt: ((): Date | null => {
            const v: any = (notice as any).expiresAt;
            if (!v) return null;
            try { return typeof v?.toDate === 'function' ? v.toDate() : new Date(v); } catch { return null; }
          })(),
        });
      }
    }
  }, [isEdit, id, notices]);

  const handleSave = async () => {
    if (!formData.title) {
      alert('タイトルを入力してください');
      return;
    }
    if (!formData.content && formData.blocks.length === 0) {
      alert('内容を入力してください');
      return;
    }
    if (!Array.isArray(formData.targets) || formData.targets.length === 0) {
      alert('表示対象を1つ以上選択してください');
      return;
    }

    setLoading(true);
    try {
      // undefined フィールドを除外
      const saveData: any = {
        title: formData.title,
        content: formData.content || '',
        blocks: formData.blocks || [],
        targets: uniq(formData.targets),
        publishedAt: formData.publishedAt,
        expiresAt: formData.expiresAt ?? undefined,
      };

      // blocks配列内のundefinedフィールドも除外（Firestoreはundefined禁止）
      if (saveData.blocks && Array.isArray(saveData.blocks)) {
        saveData.blocks = saveData.blocks.map((block: any) => {
          if (block && typeof block === 'object') {
            return Object.fromEntries(
              Object.entries(block).filter(([_, value]) => value !== undefined)
            );
          }
          return block;
        });
      }

      // undefined フィールドを完全に除外
      const cleanSaveData = Object.fromEntries(
        Object.entries(saveData).filter(([_, value]) => value !== undefined)
      );

      const legacyTarget = computeLegacyTargetFromTargets(
        Array.isArray((cleanSaveData as any).targets) ? ((cleanSaveData as any).targets as string[]) : []
      );

      if (isEdit && id) {
        const patch: any = {
          ...cleanSaveData,
          updatedAt: serverTimestamp(),
        };
        if (formData.expiresAt == null) patch.expiresAt = deleteField();
        if (legacyTarget) patch.target = legacyTarget;
        else patch.target = deleteField();
        await updateDoc(doc(db, 'importantNotices', id), patch);
      } else {
        await addDoc(collection(db, 'importantNotices'), {
          ...cleanSaveData,
          ...(legacyTarget ? { target: legacyTarget } : {}),
          createdAt: serverTimestamp(),
        });
      }
      navigate('/important-notices');
    } catch (error) {
      console.error('Failed to save important notice:', error);
      alert('保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const renderContentPreview = (content: string, blocks?: ContentBlock[]) => {
    if (blocks && blocks.length > 0) {
      return (
        <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #eee' }}>
          {renderBlocks(blocks)}
        </Box>
      );
    }
    const parts = (content || '').split(/\n+/);
    return (
      <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #eee' }}>
        {parts.map((line, idx) => {
          const urlMatch = line.match(/https?:\/\/[^\s]+/g);
          if (urlMatch && urlMatch.length === 1 && line.trim() === urlMatch[0]) {
            const url = urlMatch[0];
            const lower = url.toLowerCase();
            const isImage = lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.gif') || lower.endsWith('.webp');
            if (isImage) {
              return (
                <Box key={idx} sx={{ my: 1 }}>
                  <img src={url} alt="preview" style={{ maxWidth: '100%', borderRadius: 6 }} />
                </Box>
              );
            }
            return (
              <Box key={idx} sx={{ my: 0.5 }}>
                <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
              </Box>
            );
          }
          return (
            <Typography key={idx} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{line}</Typography>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/important-notices')}
          sx={{ mr: 2 }}
        >
          戻る
        </Button>
        <Typography variant="h4">
          {isEdit ? '重要なお知らせ編集' : '重要なお知らせ新規作成'}
        </Typography>
      </Box>

      <Card sx={{ p: 3 }}>
        <TextField
          fullWidth
          label="タイトル"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          sx={{ mb: 3 }}
        />
        <Typography variant="h6" sx={{ mb: 2 }}>内容</Typography>
        
        {/* ブロックエディタ */}
        <BlockEditor
          value={formData.blocks}
          onChange={(next) => setFormData({ ...formData, blocks: next })}
          onPickImage={async () => {
            return new Promise((resolve) => {
              setMediaOpen(true);
              const handle = (e: any) => {
                window.removeEventListener('block-editor-media', handle);
                resolve(e.detail || null);
              };
              window.addEventListener('block-editor-media', handle, { once: true });
            });
          }}
        />

        <Typography variant="subtitle2" sx={{ mb: 1, mt: 3 }}>プレビュー</Typography>
        {renderContentPreview(formData.content, formData.blocks)}

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Box sx={{ minWidth: 260 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
              表示対象（複数選択可）
            </Typography>
            <FormGroup row>
              {TARGET_PRESETS.map((o) => {
                const checked = Array.isArray(formData.targets) && formData.targets.includes(o.value);
                return (
                  <FormControlLabel
                    key={o.value}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(e) => {
                          const nextChecked = e.target.checked;
                          const current = Array.isArray(formData.targets) ? formData.targets : [];
                          const next = nextChecked ? uniq([...current, o.value]) : current.filter((v) => v !== o.value);
                          setFormData({ ...formData, targets: next });
                        }}
                      />
                    }
                    label={o.label}
                  />
                );
              })}
            </FormGroup>
          </Box>
          <DateTimePicker
            label="公開開始日時"
            value={formData.publishedAt}
            onChange={(date) => date && setFormData({ ...formData, publishedAt: date })}
            sx={{ minWidth: 250 }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <DateTimePicker
              label="公開終了日時"
              value={formData.expiresAt}
              onChange={(date) => setFormData({ ...formData, expiresAt: date ?? null })}
              disabled={formData.expiresAt == null}
              sx={{ minWidth: 250 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.expiresAt == null}
                  onChange={(e) => {
                    const isNoExpiry = e.target.checked;
                    setFormData({
                      ...formData,
                      expiresAt: isNoExpiry ? null : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                    });
                  }}
                />
              }
              label="期限なしで公開"
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/important-notices')}
            disabled={loading}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || (!formData.content && formData.blocks.length === 0)}
          >
            {loading ? '保存中...' : (isEdit ? '更新' : '作成')}
          </Button>
        </Box>
      </Card>

      <MediaLibraryDialog
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        onSelect={(item) => {
          window.dispatchEvent(new CustomEvent('block-editor-media', { detail: { url: item.url } }));
          setMediaOpen(false);
        }}
      />
    </Box>
  );
}
